/**
 * Cliente HTTP das rotas /api. Unico lugar do front que faz fetch.
 *
 * CONTRATO (lote D e dono; lote C e o Menu consomem):
 *   const api = new Api(ctx)          le a base de import.meta.env / mesma origem
 *   api.setToken(token)               passa a mandar Authorization: Bearer
 *   await api.register({ email, name, nickname, password })  -> { user, token }
 *   await api.login({ email, password })                     -> { user, token }
 *   await api.me()                                           -> user
 *   await api.ranking({ limit, offset })                     -> { rows, total }
 *   await api.myRanking()                                    -> linha | null
 *   await api.myMatches(limit)                               -> partidas
 *   api.baseUrl                       string
 *   api.wsUrl                         string  (ws:// ou wss:// conforme o protocolo)
 *
 * ERRO: toda falha vira `throw new ApiError(status, code, message)` - a tela
 * mostra `message` direto ao jogador, entao o servidor devolve texto em
 * portugues legivel, nao stack.
 */

const REQUEST_TIMEOUT_MS = 15000;

function messageForStatus(status) {
  if (status === 400) return 'Os dados enviados n\u00e3o s\u00e3o v\u00e1lidos.';
  if (status === 401) return 'Fa\u00e7a login para continuar.';
  if (status === 403) return 'Voc\u00ea n\u00e3o tem permiss\u00e3o para esta opera\u00e7\u00e3o.';
  if (status === 404) return 'O recurso solicitado n\u00e3o foi encontrado.';
  if (status === 408) return 'O servidor demorou demais para responder.';
  if (status === 409) return 'Esses dados j\u00e1 est\u00e3o cadastrados.';
  if (status === 429) return 'Muitas tentativas. Aguarde um momento e tente novamente.';
  if (status >= 500) return 'O servidor est\u00e1 indispon\u00edvel. Tente novamente em instantes.';
  return `Falha na comunica\u00e7\u00e3o com o servidor (HTTP ${status}).`;
}

export class ApiError extends Error {
  constructor(status, code, message, details = null) {
    super(message);
    this.name = 'ApiError';
    this.status = Number.isFinite(status) ? status : 0;
    this.code = code || 'unknown';
    this.details = details;
    this.field = details?.field || null;
  }
}

export class Api {
  constructor(ctx) {
    this.ctx = ctx;
    this.baseUrl = '/api';
    this.wsUrl = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws`;
    this._token = null;
  }

  setToken(token) {
    this._token = typeof token === 'string' && token ? token : null;
  }

  async register(data) {
    return this._request('/auth/register', { method: 'POST', body: data });
  }

  async login(data) {
    return this._request('/auth/login', { method: 'POST', body: data });
  }

  async providers() {
    try {
      const data = await this._request('/auth/providers');
      return Array.isArray(data?.providers) ? data.providers : [];
    } catch (err) {
      console.warn('[Api] nao foi possivel carregar provedores de login:', err);
      return [];
    }
  }

  oauthUrl(providerId) {
    return `${this.baseUrl}/auth/oauth/${encodeURIComponent(providerId)}`;
  }

  async me() {
    const data = await this._request('/auth/me');
    return data?.user ?? null;
  }

  async ranking({ limit, offset } = {}) {
    const query = new URLSearchParams();
    if (limit !== undefined && limit !== null) query.set('limit', String(limit));
    if (offset !== undefined && offset !== null) query.set('offset', String(offset));
    const suffix = query.size ? `?${query.toString()}` : '';
    return this._request(`/ranking${suffix}`);
  }

  async myRanking() {
    const data = await this._request('/ranking/me');
    return data?.row ?? null;
  }

  async myMatches(limit) {
    const query = new URLSearchParams();
    if (limit !== undefined && limit !== null) query.set('limit', String(limit));
    const suffix = query.size ? `?${query.toString()}` : '';
    const data = await this._request(`/matches/mine${suffix}`);
    return Array.isArray(data?.rows) ? data.rows : [];
  }

  async _request(path, { method = 'GET', body } = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const headers = { Accept: 'application/json' };

    if (this._token) headers.Authorization = `Bearer ${this._token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    let response;
    let text;
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal
      });
      text = await response.text();
    } catch (err) {
      throw new ApiError(0, 'network', 'Sem conex\u00e3o com o servidor.');
    } finally {
      clearTimeout(timeout);
    }

    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (err) {
        data = null;
      }
    }

    if (!response.ok) {
      const code = data?.error || `http_${response.status}`;
      const message = data?.message || messageForStatus(response.status);
      throw new ApiError(response.status, code, message, data);
    }

    if (text && data === null) {
      throw new ApiError(response.status, 'invalid_response', 'O servidor enviou uma resposta inv\u00e1lida.');
    }

    return data;
  }
}
